import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ClientDetailsResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';


interface ClientDetailsState {
  // Map of clientID -> details
  detailsByClient: Record<string, ClientDetailsResponse>;
  loading: boolean;
  error: string | null;
  // Map of clientID -> lastFetched timestamp
  lastFetchedByClient: Record<string, number>;
  // Map of clientID -> cacheInfo
  cacheInfoByClient: Record<string, CacheInfo | null>;
  setDetails: (clientID: string, details: ClientDetailsResponse) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchDetails: (
    clientID: string,
    logout: () => void,
    accessToken: string,
    forceRefresh?: boolean
  ) => Promise<void>;
  getDetails: (clientID: string) => ClientDetailsResponse | null;
  getCacheInfo: (clientID: string) => CacheInfo | null;
}

export const useClientDetailsStore = create<ClientDetailsState>()(devtools((set, get) => ({
  detailsByClient: {},
  loading: false,
  error: null,
  lastFetchedByClient: {},
  cacheInfoByClient: {},
  setDetails: (clientID, details) => set((state) => ({
    detailsByClient: { ...state.detailsByClient, [clientID]: details },
    error: null,
  })),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  getDetails: (clientID) => get().detailsByClient[clientID] || null,
  getCacheInfo: (clientID) => get().cacheInfoByClient[clientID] || null,
  fetchDetails: async (
    clientID: string,
    logout: () => void,
    accessToken: string,
    forceRefresh = false
  ) => {
    const state = get();

    // If we have cached data for this client and not forcing refresh, skip the fetch
    if (!forceRefresh && state.detailsByClient[clientID] && state.lastFetchedByClient[clientID]) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/clients/${clientID}/details`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch client details: ${response.statusText}`);
      }

      const data = await response.json();
      set((state) => ({
        detailsByClient: { ...state.detailsByClient, [clientID]: data.details },
        lastFetchedByClient: { ...state.lastFetchedByClient, [clientID]: Date.now() },
        cacheInfoByClient: { ...state.cacheInfoByClient, [clientID]: data.cacheInfo ?? null },
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'client-details-store' }));
